import React, { useState } from 'react';
import { FaFolder, FaPlus, FaShare, FaTrash, FaUpload, FaImage, FaFileArchive } from 'react-icons/fa';

const BACKEND_URL = '';

export function AlbumsView({ albums, onCreateAlbum, onViewAlbum, onShareAlbum, onDeleteAlbum, onAddPhotos, onDropPhoto, onSaveAlbumToPhotos, onDownloadAlbumZip }) {
  // Track which album card is being hovered with a dragged file (for visual feedback)
  const [dragOverAlbumId, setDragOverAlbumId] = useState(null);
  // Track per-album "Save All" loading state
  const [savingAlbumId, setSavingAlbumId] = useState(null);
  const [zippingAlbumId, setZippingAlbumId] = useState(null);

  // Always preventDefault on drag events so the browser doesn't try to navigate
  // to the dropped file's URL (which causes the "Safari can't open the page" error).
  const handleCardDragOver = (e, albumId) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
    if (dragOverAlbumId !== albumId) setDragOverAlbumId(albumId);
  };

  const handleCardDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverAlbumId(null);
  };

  const handleCardDrop = (e, album) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverAlbumId(null);
    const file = e.dataTransfer?.files?.[0];
    if (file && onDropPhoto) {
      onDropPhoto(album, file);
    }
  };

  return (
    <div data-testid="albums-view">
      <div className="mb-6">
        <button onClick={onCreateAlbum} className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700" data-testid="create-album-btn">
          <FaPlus />
          <span>Create Album</span>
        </button>
      </div>

      {albums.length === 0 ? (
        <div className="bg-white rounded-xl shadow-md p-12 text-center">
          <FaFolder className="text-gray-300 text-6xl mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-800 mb-2">No albums yet</h3>
          <p className="text-gray-600 mb-4">Create albums to organize your family photos and share them with others.</p>
          <button onClick={onCreateAlbum} className="inline-flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700">
            <FaPlus className="mr-2" />
            Create Your First Album
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {albums.map(album => {
            const isDragOver = dragOverAlbumId === album.album_id;
            return (
              <div
                key={album.album_id}
                className={`bg-white rounded-xl shadow-md overflow-hidden transition-all ${
                  isDragOver
                    ? 'ring-4 ring-blue-500 ring-offset-2 shadow-2xl scale-[1.02]'
                    : 'hover:shadow-lg'
                }`}
                data-testid={`album-card-${album.album_id}`}
                onDragEnter={(e) => handleCardDragOver(e, album.album_id)}
                onDragOver={(e) => handleCardDragOver(e, album.album_id)}
                onDragLeave={handleCardDragLeave}
                onDrop={(e) => handleCardDrop(e, album)}
              >
                <div className="aspect-video bg-gradient-to-br from-purple-500 to-blue-500 relative">
                  {album.cover_url ? (
                    <img src={`${BACKEND_URL}${album.cover_url}`} alt={album.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <FaFolder className="text-white/50 text-6xl" />
                    </div>
                  )}
                  <div className="absolute bottom-2 right-2 bg-black/50 text-white px-2 py-1 rounded text-sm">
                    {album.photo_count || 0} photos
                  </div>
                  {isDragOver && (
                    <div className="absolute inset-0 bg-blue-600/60 flex items-center justify-center pointer-events-none">
                      <div className="text-white text-center">
                        <FaUpload className="text-4xl mx-auto mb-2" />
                        <p className="font-bold">Drop to add to "{album.name}"</p>
                      </div>
                    </div>
                  )}
                </div>

                <div className="p-4">
                  <h3 className="font-bold text-lg text-gray-800 mb-1">{album.name}</h3>
                  {album.description && <p className="text-gray-600 text-sm mb-3 line-clamp-2">{album.description}</p>}

                  <div className="flex items-center justify-between pt-3 border-t">
                    <div className="flex items-center space-x-3">
                      <button onClick={() => onViewAlbum(album)} className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                        View Photos
                      </button>
                      {onAddPhotos && (
                        <button onClick={() => onAddPhotos(album)} className="flex items-center space-x-1 text-green-600 hover:text-green-800 text-sm font-medium" data-testid={`add-photos-album-${album.album_id}`}>
                          <FaUpload className="text-xs" />
                          <span>Add Photos</span>
                        </button>
                      )}
                    </div>
                    <div className="flex items-center space-x-1 flex-wrap gap-y-1">
                      {onSaveAlbumToPhotos && (album.photo_count || 0) > 0 && (
                        <button
                          onClick={async () => {
                            setSavingAlbumId(album.album_id);
                            try { await onSaveAlbumToPhotos(album); } finally { setSavingAlbumId(null); }
                          }}
                          disabled={savingAlbumId === album.album_id}
                          className="flex items-center space-x-1 px-2 py-1 text-emerald-600 hover:text-emerald-800 hover:bg-emerald-50 rounded-lg transition text-sm font-medium disabled:opacity-50"
                          title="Save all photos in this album to your device's Photos app (iPhone/Android)"
                          data-testid={`save-all-album-${album.album_id}`}
                        >
                          <FaImage className="text-sm" />
                          <span>{savingAlbumId === album.album_id ? 'Saving…' : 'Save All'}</span>
                        </button>
                      )}
                      {onDownloadAlbumZip && (album.photo_count || 0) > 0 && (
                        <button
                          onClick={async () => {
                            setZippingAlbumId(album.album_id);
                            try { await onDownloadAlbumZip(album); } finally { setZippingAlbumId(null); }
                          }}
                          disabled={zippingAlbumId === album.album_id}
                          className="flex items-center space-x-1 px-2 py-1 text-amber-700 hover:text-amber-900 hover:bg-amber-50 rounded-lg transition text-sm font-medium disabled:opacity-50"
                          title="Download all photos in this album as a ZIP file (best on desktop)"
                          data-testid={`download-zip-album-${album.album_id}`}
                        >
                          <FaFileArchive className="text-sm" />
                          <span>{zippingAlbumId === album.album_id ? 'Zipping…' : 'Download ZIP'}</span>
                        </button>
                      )}
                      <button onClick={() => onShareAlbum(album)} className="flex items-center space-x-1 px-2 py-1 text-purple-600 hover:text-purple-800 hover:bg-purple-50 rounded-lg transition text-sm font-medium" title="Share album" data-testid={`share-album-${album.album_id}`}>
                        <FaShare className="text-sm" />
                        <span>Share</span>
                      </button>
                      <button onClick={() => onDeleteAlbum(album.album_id, album.name)} className="flex items-center space-x-1 px-2 py-1 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition text-sm" title="Delete album" aria-label="Delete album" data-testid={`delete-album-${album.album_id}`}>
                        <FaTrash className="text-sm" />
                        <span className="md:hidden">Delete</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
